import {useState, useEffect, cloneElement, useRef} from 'react';
import { Popover, Empty } from 'antd';
import '../style/QuantitySuggestions.scss';

/**
 * Generate quantity suggestions based on current value and unit
 * @param {number} currentValue - Current input value
 * @param {string} unit - Product unit (kg, item, piece, etc.)
 * @returns {number[]} Array of suggested quantities
 */
const generateSuggestions = (currentValue, unit) => {
    const suggestions = [];

    if (unit === 'kg') {
        // For kg: suggest increments of 0.1
        // If user typed 0., suggest 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5
        if (currentValue === 0 || currentValue === '') {
            return [0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.75, 1];
        } else if (currentValue < 0.1) {
            return [0.1, 0.15, 0.2, 0.25, 0.3];
        } else if (currentValue < 0.5) {
            const base = Math.floor(currentValue * 10) / 10;
            return [
                base + 0.1,
                base + 0.2,
                base + 0.3,
                base + 0.5,
                base + 1
            ];
        } else if (currentValue < 1) {
            return [1, 1.5, 2, 2.5, 3];
        }
        else
        {
            const base = Math.floor(currentValue);
            return [
                base + 1,
                base + 2,
                base + 3,
                base + 4,
                base + 5,
                base + 0.1,
                base + 0.2,
                base + 0.3,
                base + 0.4,
                base + 0.5,
                base + 11,
                base + 12,
                base + 13,
                base + 14,
            ];
        }
    } else {
        // For other units: suggest integer increments
        if (currentValue === 0 || currentValue === '')
        {
            return [1, 2, 3, 4, 5, 10, 20, 30, 40, 50];
        }
        else if (currentValue < 5)
        {
            return [
                currentValue + 1,
                currentValue + 2,
                currentValue + 3,
                currentValue + 4,
                currentValue + 5,
                currentValue + 10
            ];
        }
        else if (currentValue < 20)
        {
            return [currentValue + 5, currentValue + 10, currentValue + 20, currentValue + 50];
        }
        else
        {
            return [currentValue + 10, currentValue + 20, currentValue + 50, currentValue + 100];
        }
    }
};

const QuantitySuggestions = ({ value, unit, onChange, autoOpen = false, children }) => {
    const [isOpen, setIsOpen] = useState(autoOpen);
    const [currentValue, setCurrentValue] = useState(value);
    const isClickingSuggestion = useRef(false);

    // Update currentValue when value prop changes
    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    // Generate suggestions based on current input value
    const suggestions = generateSuggestions(currentValue, unit);

    // Sync isOpen with autoOpen prop
    useEffect(() => {
        if (autoOpen) {
            setIsOpen(true);
        }
    }, [autoOpen]);

    const handleSuggestionClick = (suggestedValue) => {
        // Immediately call onChange before closing popover
        onChange(suggestedValue);
        // Mark that we just clicked a suggestion
        isClickingSuggestion.current = true;
        // Close popover after a slight delay to ensure onChange is processed
        setTimeout(() => {
            setIsOpen(false);
            isClickingSuggestion.current = false;
        }, 50);
    };

    // Handle input focus - keep popover open
    const handleInputFocus = (e) => {
        setIsOpen(true);
        // Call original onFocus if exists
        if (children.props.onFocus) {
            children.props.onFocus(e);
        }
    };

    // Handle input blur - keep popover open (don't close on blur)
    const handleInputBlur = (e) => {
        // Don't close popover on blur - only close when clicking outside
        // Call original onBlur if exists
        if (children.props.onBlur) {
            children.props.onBlur(e);
        }
    };

    // Handle popover visibility change
    const handleOpenChange = (open) => {
        // Don't close popover if we just clicked a suggestion
        if (!open && isClickingSuggestion.current) {
            return;
        }
        setIsOpen(open);
    };

    const content = (
        <div className="quantity-suggestions">
            <div className="suggestions-title">
                {unit === 'kg' ? 'Gợi ý (Kg)' : 'Gợi ý số lượng'}
            </div>
            <div className="suggestions-list">
                {suggestions && suggestions.length > 0 ? (
                    suggestions.map((suggestion, index) => (
                        <button
                            key={index}
                            className="suggestion-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleSuggestionClick(suggestion);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {unit === 'kg' ? suggestion.toFixed(2) : suggestion}
                        </button>
                    ))
                ) : (
                    <Empty description="Không có gợi ý" style={{ padding: '10px' }} />
                )}
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="focus"
            placement="topRight"
            open={isOpen}
            onOpenChange={handleOpenChange}
            overlayClassName="quantity-suggestions-popover"
            destroyTooltipOnHide={false}
            getPopupContainer={(trigger) => trigger.parentElement}
        >
            {cloneElement(children, {
                onFocus: handleInputFocus,
                onBlur: handleInputBlur,
            })}
        </Popover>
    );
};

export default QuantitySuggestions;
